//
// # Layer Picker Menu
//
// A context menu that shows available metal layers for switching mid-draw
// or changing an existing route segment's layer.
// Reuses the same theme/styling pattern as RouteContextMenu.
//

import type { Editor } from "./editor";
import { Palette } from "DrawCore/src/style";
import { DrawRoute } from "EditorCore/src/content/layout/draw_route";

export class LayerPickerMenu {
  private menuElement: HTMLDivElement | null = null;
  private styleSheet: HTMLStyleElement | null = null;

  constructor(private readonly editor: Editor) {
    this.setupGlobalClickHandler();
  }

  // Show the layer picker for mid-draw layer switching.
  showForDrawRoute = (
    drawRoute: DrawRoute,
    pageX: number,
    pageY: number
  ): void => {
    const metals = drawRoute.metalLayers;
    if (metals.length === 0) return;

    this.ensureElement();
    if (!this.menuElement) return;

    const activeIdx = drawRoute.activeLayerIndex;

    let html = `<div class="lp-header">Switch Layer</div>`;
    html += `<div class="lp-separator"></div>`;
    html += `<div class="lp-items">`;
    for (let i = 0; i < metals.length; i++) {
      const label = drawRoute.layerLabel(metals[i]);
      const dir = metals[i].direction ?? "";
      const active = i === activeIdx ? " lp-active" : "";
      const check = i === activeIdx ? ">> " : "";
      html += `<div class="lp-item${active}" data-layer-idx="${i}">${check}${label} <span class="lp-dir">(${dir})</span></div>`;
    }
    html += `</div>`;

    this.menuElement.innerHTML = html;
    this.menuElement.style.display = "block";
    this.positionMenu(pageX, pageY);

    // Wire click handlers
    this.menuElement.querySelectorAll(".lp-item").forEach((item) => {
      const el = item as HTMLElement;
      el.onclick = (e) => {
        e.stopPropagation();
        const idx = parseInt(el.getAttribute("data-layer-idx") ?? "-1", 10);
        if (idx >= 0) {
          drawRoute.switchToLayerByIndex(idx);
        }
        this.hide();
      };
    });
  };

  hide = (): void => {
    if (this.menuElement) this.menuElement.style.display = "none";
  };

  isVisible = (): boolean => {
    return (
      this.menuElement !== null && this.menuElement.style.display !== "none"
    );
  };

  updateTheme = (): void => {
    this.applyMenuStyles();
    this.updateStyleSheet();
  };

  dispose = (): void => {
    if (this.menuElement) {
      this.menuElement.remove();
      this.menuElement = null;
    }
  };

  private ensureElement = (): void => {
    if (!this.menuElement) {
      this.menuElement = document.createElement("div");
      this.setupMenuStyles();
      document.body.appendChild(this.menuElement);
    }
  };

  private setupMenuStyles = (): void => {
    if (!this.menuElement) return;
    this.applyMenuStyles();
    this.styleSheet = document.createElement("style");
    this.updateStyleSheet();
    document.head.appendChild(this.styleSheet);
  };

  private applyMenuStyles = (): void => {
    if (!this.menuElement) return;
    const p = Palette.getAll();
    const s = this.menuElement.style;
    s.position = "absolute";
    s.backgroundColor = p.background;
    s.color = p.text;
    s.border = `1px solid ${p.border}`;
    s.borderRadius = "4px";
    s.padding = "0";
    s.fontSize = "12px";
    s.fontFamily = "Monaco, 'Courier New', monospace";
    s.minWidth = "140px";
    s.zIndex = "10000";
    s.boxShadow = p.boxShadow;
    s.display = "none";
  };

  private updateStyleSheet = (): void => {
    if (!this.styleSheet) return;
    const p = Palette.getAll();
    this.styleSheet.textContent = `
      .lp-header { padding: 6px 12px; font-weight: bold; color: ${p.textHeader}; font-size: 11px; }
      .lp-separator { height: 1px; background-color: ${p.border}; margin: 2px 0; }
      .lp-items { padding: 4px 0; }
      .lp-item { padding: 5px 12px; cursor: pointer; user-select: none; }
      .lp-item:hover { background-color: ${p.backgroundHover}; }
      .lp-item.lp-active { font-weight: bold; color: ${p.textHeader}; }
      .lp-dir { color: ${p.textSection}; font-size: 10px; }
    `;
  };

  private positionMenu = (pageX: number, pageY: number): void => {
    if (!this.menuElement) return;
    this.menuElement.style.left = `${pageX + 2}px`;
    this.menuElement.style.top = `${pageY + 2}px`;
    const rect = this.menuElement.getBoundingClientRect();
    if (rect.right > window.innerWidth)
      this.menuElement.style.left = `${Math.max(10, pageX - rect.width - 2)}px`;
    if (rect.bottom > window.innerHeight)
      this.menuElement.style.top = `${Math.max(10, pageY - rect.height - 2)}px`;
  };

  private setupGlobalClickHandler = (): void => {
    document.addEventListener("mousedown", (e) => {
      if (
        this.menuElement &&
        this.menuElement.style.display !== "none" &&
        !this.menuElement.contains(e.target as Node)
      ) {
        this.hide();
      }
    });
  };
}
